import { Button, Input } from '@artosera/ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { IDENTIFIANTS_DEMO, modeDemonstrationActif } from '../../api/demo.js';
import { authApi } from '../../api/auth.js';
import { estApiError, estErreurReseau } from '../../api/errors.js';
import { useApresConnexion } from '../../auth/session.js';
import { AuthCard } from '../AuthCard.js';
import { Field } from '../Field.js';
import { allerApresConnexion, useContexteConnexion } from './connexionFlow.js';
import styles from './ConnexionPage.module.css';
import { retourSur } from '../../auth/retour.js';

/**
 * `/connexion/2fa` : second facteur du login (pas l'élévation de session,
 * `docs/dev/api.md` § 3 — le login se rejoue avec le code, il n'y a pas de
 * session ouverte tant que ce n'est pas validé).
 */
export function Connexion2FAPage(): JSX.Element {
  const { t } = useTranslation('regie');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { identifiantsEnAttente } = useContexteConnexion();
  const enAttente = identifiantsEnAttente ?? (modeDemonstrationActif() ? IDENTIFIANTS_DEMO : null);
  const apresConnexion = useApresConnexion();

  const [codeTotp, setCodeTotp] = useState('');
  const [codeSecours, setCodeSecours] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  // Jamais une adresse d'un autre site (`auth/retour.ts`).
  const retour = retourSur(searchParams.get('retour'));

  useEffect(() => {
    if (!enAttente) {
      navigate('/connexion', { replace: true });
    }
  }, [enAttente, navigate]);

  if (!enAttente) {
    return (
      <AuthCard title={t('auth.deuxFacteurs.titre')}>
        <p className={styles.statut}>{t('auth.deuxFacteurs.sessionExpiree')}</p>
      </AuthCard>
    );
  }

  async function soumettre(): Promise<void> {
    if (!enAttente) return;
    setErreur(null);
    setEnCours(true);
    try {
      await authApi.connexion({
        email: enAttente.email,
        motDePasse: enAttente.motDePasse,
        ...(codeTotp ? { codeTotp } : {}),
        ...(codeSecours ? { codeSecours } : {}),
      });
      await apresConnexion();
      await allerApresConnexion(navigate, retour);
    } catch (error) {
      if (estApiError(error)) {
        setErreur(error.message);
      } else if (estErreurReseau(error)) {
        setErreur(t('auth.connexion.erreurReseau'));
      } else {
        setErreur(t('commun.erreurReseau'));
      }
    } finally {
      setEnCours(false);
    }
  }

  return (
    <AuthCard title={t('auth.deuxFacteurs.titre')} subtitle={t('auth.deuxFacteurs.sousTitre')}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void soumettre();
        }}
        noValidate
      >
        <Field
          label={t('auth.deuxFacteurs.champCode')}
          htmlFor="deux-facteurs-code"
          className={styles.champCode}
        >
          <Input
            id="deux-facteurs-code"
            maxLength={6}
            pattern="[0-9]*"
            inputMode="numeric"
            autoComplete="one-time-code"
            mono
            className={styles.pleineLargeur}
            value={codeTotp}
            onChange={(event) => setCodeTotp(event.target.value)}
          />
        </Field>
        <Field label={t('auth.deuxFacteurs.champCodeSecours')} htmlFor="deux-facteurs-secours">
          <Input
            id="deux-facteurs-secours"
            mono
            className={styles.pleineLargeur}
            value={codeSecours}
            onChange={(event) => setCodeSecours(event.target.value)}
          />
        </Field>

        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}

        <Button type="submit" className={styles.envoi} loading={enCours}>
          {t('auth.deuxFacteurs.bouton')}
        </Button>
      </form>

      <p className={styles.lienOubli}>
        <Link to="/connexion">{t('auth.deuxFacteurs.retour')}</Link>
      </p>
    </AuthCard>
  );
}
